import { arrayMove } from "@dnd-kit/sortable";
import type { SortingStrategy } from "@dnd-kit/sortable";

type TileSpan = { colSpan: number; rowSpan: number };

interface GridPosition {
  col: number;     // 1-based
  row: number;     // 1-based
  colSpan: number; // clamped to columns
  rowSpan: number;
}

/**
 * Simulate CSS `grid-auto-flow: dense` placement.
 *
 * Each tile declares a `span` (no explicit start position), so the browser
 * tries every cell from the top-left and places the tile at the earliest
 * position where its entire bounding box is free — restarting from (1,1)
 * for each tile (dense mode).
 *
 * This function replicates that algorithm exactly, giving us the 1-based
 * col/row for every tile in the array.
 */
function computeGridPositions(
  tiles: ReadonlyArray<TileSpan>,
  columns: number
): GridPosition[] {
  const occupied = new Set<string>();
  const positions: GridPosition[] = [];

  for (const tile of tiles) {
    const span = Math.min(tile.colSpan, columns);
    let placed = false;
    let row = 1;

    while (!placed) {
      for (let col = 1; col <= columns - span + 1; col++) {
        // Check that every cell in the tile's bounding box is free.
        let fits = true;
        outer: for (let r = row; r < row + tile.rowSpan; r++) {
          for (let c = col; c < col + span; c++) {
            if (occupied.has(`${r},${c}`)) { fits = false; break outer; }
          }
        }

        if (fits) {
          positions.push({ col, row, colSpan: span, rowSpan: tile.rowSpan });
          for (let r = row; r < row + tile.rowSpan; r++)
            for (let c = col; c < col + span; c++)
              occupied.add(`${r},${c}`);
          placed = true;
          break;
        }
      }
      if (!placed) row++;
    }
  }

  return positions;
}

/**
 * Map a tile's original array index to its index in the post-arrayMove array.
 *
 * arrayMove semantics (dragging forward, e.g. 0→2):
 *   [A,B,C,D] → [B,C,A,D]
 *   A: 0→2   B: 1→0   C: 2→1   D: 3→3
 */
function originalToReorderedIndex(
  i: number,
  activeIndex: number,
  overIndex: number
): number {
  if (i === activeIndex) return overIndex;
  if (overIndex > activeIndex && i > activeIndex && i <= overIndex) return i - 1;
  if (overIndex < activeIndex && i < activeIndex && i >= overIndex) return i + 1;
  return i;
}

/**
 * Factory that creates a `SortingStrategy` aware of each tile's colSpan/rowSpan.
 *
 * Instead of approximating with neighbor rects, this strategy:
 *  1. Pre-computes the original grid positions (dense auto-placement).
 *  2. Simulates `arrayMove(tiles, activeIndex, overIndex)` for every new
 *     hover target and computes grid positions for that hypothetical layout.
 *  3. Returns the pixel delta between the tile's current measured rect and
 *     where it would land in the simulated layout — giving a pixel-perfect
 *     preview that mirrors what the grid will look like on drop.
 *
 * The cache is per (activeIndex, overIndex) pair so the expensive simulation
 * runs once per move event, not once per tile per event.
 *
 * @param tiles    The current visible tile array (frozen during drag).
 * @param columns  Number of grid columns.
 * @param gap      Grid gap in px (default 8 = gap-2).
 * @param rowHeight Fixed row height in px (default 100 = gridAutoRows: "100px").
 */
export function createGridSortingStrategy(
  tiles: ReadonlyArray<TileSpan>,
  columns: number,
  gap = 8,
  rowHeight = 100
): SortingStrategy {
  // Pre-compute original positions once — tiles are frozen during drag.
  const originalPositions = computeGridPositions(tiles, columns);

  // Per-(activeIndex, overIndex) cache so we only simulate once per move.
  let cache: {
    activeIndex: number;
    overIndex: number;
    reorderedPositions: GridPosition[];
    colWidth: number;
    containerLeft: number;
    containerTop: number;
  } | null = null;

  return ({ activeNodeRect, activeIndex, overIndex, index, rects }) => {
    if (
      !activeNodeRect ||
      activeIndex === -1 ||
      overIndex === -1 ||
      activeIndex === overIndex
    ) {
      return null;
    }

    // Active tile is invisible in the grid (rendered by DragOverlay).
    if (index === activeIndex) return null;

    // Rebuild simulation when the hover target changes.
    if (
      !cache ||
      cache.activeIndex !== activeIndex ||
      cache.overIndex !== overIndex
    ) {
      // Derive column width and the grid container's viewport origin from the
      // measured rects.  For any tile: width = span * colWidth + (span-1) * gap.
      let colWidth = 0;
      let containerLeft = 0;
      let containerTop = 0;

      for (let i = 0; i < tiles.length; i++) {
        const rect = rects[i];
        const pos = originalPositions[i];
        if (!rect || !pos) continue;
        const span = pos.colSpan;
        colWidth = (rect.width - (span - 1) * gap) / span;
        containerLeft = rect.left - (pos.col - 1) * (colWidth + gap);
        containerTop = rect.top - (pos.row - 1) * (rowHeight + gap);
        break;
      }

      // Simulate the layout after the pending drop.
      const reorderedTiles = arrayMove(
        tiles as TileSpan[],
        activeIndex,
        overIndex
      );
      const reorderedPositions = computeGridPositions(reorderedTiles, columns);

      cache = {
        activeIndex,
        overIndex,
        reorderedPositions,
        colWidth,
        containerLeft,
        containerTop,
      };
    }

    // Where does this tile end up in the simulated reorder?
    const reorderedIdx = originalToReorderedIndex(index, activeIndex, overIndex);
    const newPos = cache.reorderedPositions[reorderedIdx];
    const currentRect = rects[index];

    if (!newPos || !currentRect) {
      return { x: 0, y: 0, scaleX: 1, scaleY: 1 };
    }

    // Pixel position this tile would occupy in the simulated layout.
    const newLeft = cache.containerLeft + (newPos.col - 1) * (cache.colWidth + gap);
    const newTop  = cache.containerTop  + (newPos.row - 1) * (rowHeight + gap);

    return {
      x: newLeft - currentRect.left,
      y: newTop  - currentRect.top,
      scaleX: 1,
      scaleY: 1,
    };
  };
}
